/**
 * Persistence for the 7-phase client setup — the real /onboarding/* contract.
 *
 * Zero-tenant state (the package gate before a business exists) never calls
 * these; it lives in the same-browser zustand cache. Every function here
 * takes the tenant (website domain) and sends it as the `tenant_id` query
 * param, per the contract.
 *
 * Server owns: package, setup (name + locations), prove-site (authenticated
 * retrieval — a saved key alone is a false pass), progress ticks, and
 * the go-live complete gate (409 when can_go_live is false).
 *
 * Contract: docs/research/onboarding/INTEGRATION-CONTRACT.md
 */

import { apiClient } from '@/services/apiClient';
import { apiService } from '@/services/api';
import { createWorkspace, sendInvitation } from '@/services/authApi';
import { createLogger } from '@/lib/logger';
import { normalizeWebsite } from '@/lib/domain';
import type { ProductPackage, Workspace } from '@/types/auth';
import { HUB_JOBS, type HubJob } from './jobs';

const log = createLogger('onboarding');

function httpStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

function logFail(action: string, tenantId: string, err: unknown): void {
  log.warn(`${action} failed`, { tenantId, status: httpStatus(err) });
}

/** Raw server shape (snake_case) for GET /onboarding/status. */
interface RawOnboardingStatus {
  already_on_file: boolean;
  /** May be absent on an older backend — see the fallback exclude in the page. */
  completed?: boolean;
  package: ProductPackage | null;
  completed_jobs: string[];
  wp_key_present: boolean;
  proved: boolean;
  site_url: string | null;
  just_me: boolean;
  can_go_live: boolean;
}

export interface OnboardingStatus {
  /** A Brand Bible row already exists — this client is not new, exclude them. */
  alreadyOnFile: boolean;
  /** settings.onboarding.completed_at is set. Undefined when the backend is
   *  older than the contract — callers fall back to the bible-row exclude. */
  completed?: boolean;
  package: ProductPackage | null;
  completedJobs: HubJob[];
  wpKeyPresent: boolean;
  /** Last prove retrieval succeeded — the site answered. */
  proved: boolean;
  /** URL the retrieval returned; not a post we created. */
  siteUrl: string | null;
  justMe: boolean;
  /** Required jobs done per the server; invite never blocks. */
  canGoLive: boolean;
}

const asHubJobs = (jobs: string[]): HubJob[] =>
  jobs.filter((job): job is HubJob => (HUB_JOBS as readonly string[]).includes(job));

export async function fetchOnboardingStatus(tenantId: string): Promise<OnboardingStatus> {
  try {
    const { data } = await apiClient.get<RawOnboardingStatus>('/onboarding/status', {
      params: { tenant_id: tenantId },
    });
    log.debug('status ok', {
      tenantId,
      alreadyOnFile: data.already_on_file,
      completed: data.completed,
      package: data.package,
      jobs: data.completed_jobs,
    });
    return {
      alreadyOnFile: data.already_on_file,
      completed: data.completed,
      package: data.package,
      completedJobs: asHubJobs(data.completed_jobs ?? []),
      wpKeyPresent: data.wp_key_present,
      proved: data.proved,
      siteUrl: data.site_url,
      justMe: data.just_me,
      canGoLive: data.can_go_live,
    };
  } catch (err) {
    logFail('status', tenantId, err);
    throw err;
  }
}

export async function savePackage(tenantId: string, pkg: ProductPackage): Promise<void> {
  try {
    await apiClient.post('/onboarding/package', { product_package: pkg }, {
      params: { tenant_id: tenantId },
    });
    log.info('package saved', { tenantId, pkg });
  } catch (err) {
    logFail('package', tenantId, err);
    throw err;
  }
}

export async function saveSetup(
  tenantId: string,
  companyName: string,
  serviceLocations: string[],
): Promise<void> {
  try {
    await apiClient.post(
      '/onboarding/setup',
      { company_name: companyName.trim(), service_locations: serviceLocations },
      { params: { tenant_id: tenantId } },
    );
    log.info('setup saved', { tenantId, locations: serviceLocations.length });
  } catch (err) {
    logFail('setup', tenantId, err);
    throw err;
  }
}

export interface ProveSiteResult {
  ok: boolean;
  site_url: string | null;
}

/**
 * Prove = RETRIEVAL, not a WordPress write. The server saves the key (Core
 * DB only) and does an authenticated GET against the client site. Pass = the
 * site answers (ok). Fail = missing/wrong key or WP unreachable. Save-key
 * alone is a false pass — there is no draft to publish and we never call
 * POST /wordpress/posts.
 */
export async function proveSite(
  tenantId: string,
  secretKey?: string,
): Promise<ProveSiteResult> {
  try {
    const { data } = await apiClient.post<ProveSiteResult>(
      '/onboarding/prove-site',
      { secret_key: secretKey?.trim() || undefined },
      { params: { tenant_id: tenantId } },
    );
    if (!data.ok) {
      throw new Error('prove-site retrieval did not pass');
    }
    log.info('prove ok', { tenantId, siteUrl: data.site_url });
    return data;
  } catch (err) {
    logFail('prove-site', tenantId, err);
    throw err;
  }
}

/** Progress ticks with no dedicated write: invite/just-me, foundation, crawl, ads. */
export interface ProgressPatch {
  completedJobs?: HubJob[];
  justMe?: boolean;
  foundationDone?: string[];
}

export async function patchProgress(tenantId: string, patch: ProgressPatch): Promise<void> {
  const body: Record<string, unknown> = {};
  if (patch.completedJobs !== undefined) body.completed_jobs = patch.completedJobs;
  if (patch.justMe !== undefined) body.just_me = patch.justMe;
  if (patch.foundationDone !== undefined) body.foundation_done = patch.foundationDone;
  try {
    await apiClient.patch('/onboarding/progress', body, {
      params: { tenant_id: tenantId },
    });
    log.debug('progress patched', { tenantId, keys: Object.keys(body) });
  } catch (err) {
    logFail('progress', tenantId, err);
    throw err;
  }
}

/** Go-live. 409 when the server says a required job is still open. */
export async function completeOnboarding(tenantId: string): Promise<void> {
  try {
    await apiClient.post('/onboarding/complete', null, {
      params: { tenant_id: tenantId },
    });
    log.info('complete ok', { tenantId });
  } catch (err) {
    logFail('complete', tenantId, err);
    throw err;
  }
}

/**
 * Phase-1 convenience: save the plugin key into Core (POST
 * /api/plugin/save-key path). This is a Core DB write only — it is NOT proof
 * of connection and never marks the prove job. Prove stays retrieval-only.
 */
export async function savePluginKey(tenantId: string, key: string): Promise<void> {
  await apiService.saveWordPressKey(key.trim(), tenantId);
}

export async function createBusiness(name: string, domain: string): Promise<Workspace> {
  const website = normalizeWebsite(domain);
  if (!name.trim()) throw new Error('Business name is required');
  if (!website) throw new Error('Website is required');
  return createWorkspace({ name: name.trim(), website });
}

export async function inviteTeammate(workspaceId: string, email: string): Promise<void> {
  await sendInvitation(workspaceId, { email: email.trim(), role: 'editor' });
}
